import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    Checkbox,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Download as DownloadIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Error as ErrorIcon,
} from '@mui/icons-material';
import { saveAs } from 'file-saver';
import { version } from 'process';
import { approveChecklistScheduleEvent, getChecklistResponsesByEvent } from 'core/API/checklist';
import { useToastContext } from 'core/components/Toast/context/ToastContext';

// Define the structure of a checklist item
interface ChecklistItem {
    id: string;
    section: string;
    duty: string;
    instructions: string;
    answerType: string;
    correctAnswer: string;
    submittedAnswer: string;
    comment: string;
    picLink: string;
    isEditing: boolean;
    isValid: {
        comment: boolean;
    };
    errors: {
        comment?: string;
    };
    isCorrect?: boolean; // Optional field to store correctness
}

// Define column validation rules
interface ValidationRule {
    required?: boolean;
    maxLength?: number;
}

interface TemplatePageProps {
    radioValue?: string;
    dropdownValue?: string;
    onBackClick?: () => void;
    initialData?: ChecklistItem[];
    accessibility?: string;
    selectedItem?: any; // Add selectedItem prop
    onApprovalSuccess?: () => void; // Callback to refresh data after approval
    canApprove?: boolean; // Whether the current user is allowed to approve this checklist
}

const ApprovalTemplatePage: React.FC<TemplatePageProps> = ({
    radioValue,
    dropdownValue,
    onBackClick,
    initialData = [],
    accessibility,
    selectedItem,
    onApprovalSuccess,
    canApprove = true,
}) => {
    const { showToast } = useToastContext();
    
    const columns = [
        { id: 'section', header: 'Section' },
        { id: 'duty', header: 'Duty' },
        { id: 'instructions', header: 'Instructions' },
        { id: 'answerType', header: 'Answer' },
        { id: 'correctAnswer', header: 'Correct' },
        { id: 'submittedAnswer', header: 'Submitted' },
        { id: 'picLink', header: 'Picture' },
    ];

    const [items, setItems] = useState<ChecklistItem[]>([]);

    const cellRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
    const [activeCell, setActiveCell] = useState<{ rowIndex: number; colId: string } | null>(null);

    useEffect(() => {
        const fetchChecklistResponses = async () => {
            // If we have a selectedItem with an id, fetch checklist responses
            if (selectedItem?.id) {
                try {
                    const responseData = await getChecklistResponsesByEvent(selectedItem.id);
                    if (responseData?.member && responseData.member.length > 0) {
                        // Map response data to checklist items
                        const mappedItems = responseData.member.map((response: any, index: number) => {
                            const task = response.task || {};
                            const expectedAnswer = task.expectedAnswer || response.expectedAnswer || 'N/A';
                            const submittedAnswer = response.answer || 'N/A';
                            const isCorrect = expectedAnswer === submittedAnswer;
                            
                            return {
                                id: (index + 1).toString(),
                                section: task.section || 'Unknown Section',
                                duty: task.duty || 'Unknown Duty',
                                instructions: task.instructions || 'No instructions',
                                answerType: task.answerType || 'True/False',
                                correctAnswer: expectedAnswer,
                                submittedAnswer: submittedAnswer,
                                comment: response.reason || 'No comments',
                                picLink: '', // Picture link if available in response
                                isEditing: false,
                                isValid: { comment: true },
                                errors: {},
                                isCorrect: isCorrect, // Store correctness for display
                            };
                        });
                        setItems(mappedItems);
                        return;
                    }
                } catch (error) {
                    console.error('Error fetching checklist responses:', error);
                    // Fall through to other data sources
                }
            }
            
            // Fallback to existing logic
            if (initialData.length > 0) {
                const validatedItems = initialData.map((item) => {
                    const { updatedItem } = validateRow({
                        ...item,
                        comment: item.comment || '',
                        picLink: item.picLink || '',
                        isEditing: false,
                    }, false);
                    return updatedItem;
                });
                setItems(validatedItems);
            } else if (selectedItem?.checklistTemplate?.tasks && selectedItem.checklistTemplate.tasks.length > 0) {
                // Use tasks from the selected approval checklist
                const mappedTasks = selectedItem.checklistTemplate.tasks.map((task: any, index: number) => ({
                    id: (index + 1).toString(),
                    section: task.section || 'Unknown Section',
                    duty: task.duty || 'Unknown Duty',
                    instructions: task.instructions || 'No instructions',
                    answerType: task.answerType || 'True/False',
                    correctAnswer: task.expectedAnswer || 'N/A',
                    submittedAnswer: task.expectedAnswer || 'N/A', // For approval checklists, submitted answer is same as expected
                    comment: task.comment || 'No comments',
                    picLink: task.picLink || '',
                    isEditing: false,
                    isValid: { comment: true },
                    errors: {},
                }));
                setItems(mappedTasks);
            } else {
                // Dummy data for demonstration
                setItems([
                    {
                        id: '1',
                        section: 'Safety',
                        duty: 'Fire Extinguisher Check',
                        instructions: 'Ensure all extinguishers are accessible',
                        answerType: 'True/False',
                        correctAnswer: 'True',
                        submittedAnswer: 'True',
                        comment: 'All units operational',
                        picLink: 'http://example.com/pic1.jpg',
                        isEditing: false,
                        isValid: { comment: true },
                        errors: {},
                    },
                    {
                        id: '2',
                        section: 'Equipment',
                        duty: 'Inspect machinery',
                        instructions: 'Check for wear and tear',
                        answerType: 'Checkbox',
                        correctAnswer: 'Checked',
                        submittedAnswer: 'Unchecked',
                        comment: 'Minor wear detected',
                        picLink: 'http://example.com/pic2.jpg',
                        isEditing: false,
                        isValid: { comment: true },
                        errors: {},
                    },
                    {
                        id: '3',
                        section: 'Environment',
                        duty: 'Verify lighting',
                        instructions: 'Ensure all lights are working',
                        answerType: 'True/False',
                        correctAnswer: 'True',
                        submittedAnswer: 'False',
                        comment: 'Two bulbs need replacement',
                        picLink: 'http://example.com/pic3.jpg',
                        isEditing: false,
                        isValid: { comment: true },
                        errors: {},
                    },
                ]);
            }
        };
        
        fetchChecklistResponses();
    }, [initialData, selectedItem]);

    const validateCell = (
        value: string,
        columnId: string
    ): { valid: boolean; message?: string } => {
        const validation: ValidationRule = { required: false, maxLength: 256 };
        if (validation.required && !value.trim()) return { valid: false, message: 'This field is required' };
        if (validation.maxLength && value.trim().length > validation.maxLength)
            return { valid: false, message: `Must be no more than ${validation.maxLength} characters` };
        return { valid: true };
    };

    const validateRow = (
        item: ChecklistItem,
        updateItem: boolean = true
    ): { valid: boolean; updatedItem: ChecklistItem } => {
        const validation = validateCell(item.comment, 'comment');
        const updatedItem = {
            ...item,
            isValid: { comment: validation.valid },
            errors: { comment: validation.valid ? undefined : validation.message },
        };
        if (updateItem) {
            setItems((prevItems) =>
                prevItems.map((i) => (i.id === item.id ? updatedItem : i))
            );
        }
        return { valid: validation.valid, updatedItem };
    };

    const validateAllCells = (): { isValid: boolean; invalidRowIndices: number[] } => {
        let allValid = true;
        const invalidRowIndices: number[] = [];
        const newItems = items.map((item, index) => {
            const { valid, updatedItem } = validateRow(item, false);
            if (!valid) {
                allValid = false;
                invalidRowIndices.push(index);
            }
            return updatedItem;
        });
        setItems(newItems);
        return { isValid: allValid, invalidRowIndices };
    };

    const toggleEditMode = (id: string) => {
        setItems((prevItems) =>
            prevItems.map((item) =>
                item.id === id ? { ...item, isEditing: !item.isEditing } : item
            )
        );
    };

    const updateField = (id: string, field: string, value: string) => {
        setItems((prevItems) =>
            prevItems.map((item) => {
                if (item.id !== id) return item;
                const validation = validateCell(value, field);
                return {
                    ...item,
                    [field]: value,
                    isValid: { ...item.isValid, [field]: validation.valid },
                    errors: { ...item.errors, [field]: validation.valid ? undefined : validation.message },
                };
            })
        );
    };

    const handleCellFocus = (rowIndex: number, colId: string) => {
        setActiveCell({ rowIndex, colId });
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, rowIndex: number) => {
        if (!activeCell) return;
        const { key } = e;
        let nextRow = activeCell.rowIndex;
        const colId = 'comment';

        switch (key) {
            case 'ArrowUp':
                if (rowIndex > 0) {
                    nextRow = rowIndex - 1;
                }
                e.preventDefault();
                break;
            case 'ArrowDown':
            case 'Enter':
                if (rowIndex < items.length - 1) {
                    nextRow = rowIndex + 1;
                }
                e.preventDefault();
                break;
            case 'Tab':
                if (!e.shiftKey && rowIndex < items.length - 1) {
                    nextRow = rowIndex + 1;
                } else if (e.shiftKey && rowIndex > 0) {
                    nextRow = rowIndex - 1;
                }
                e.preventDefault();
                break;
            default:
                return;
        }

        if (nextRow !== rowIndex) {
            setTimeout(() => {
                const cellRefKey = `${nextRow}-${colId}`;
                const nextCellRef = cellRefs.current[cellRefKey];
                if (nextCellRef) nextCellRef.focus();
                setActiveCell({ rowIndex: nextRow, colId });
            }, 0);
        }
    };

    const getDataAsJson = () =>
        items.map((item) => ({
            section: item.section,
            duty: item.duty,
            instructions: item.instructions,
            answer: item.answerType,
            correct: item.correctAnswer,
            submitted: item.submittedAnswer,
            comments: item.comment,
            picture: item.picLink,
        }));

    const downloadJson = () => {
        const jsonData = getDataAsJson();
        const jsonString = JSON.stringify(jsonData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        saveAs(blob, 'checklist_data.json');
    };


    const templateTitle = radioValue ? `${radioValue} Checklist Template` : 'Checklist Template';

    const headerData = {
        version: selectedItem?.checklistTemplate?.version || 1,
        checklistName: selectedItem?.checklistTemplate?.label || radioValue || 'Safety Inspection',
        category: selectedItem?.checklistTemplate?.category?.label || dropdownValue || 'Compliance',
        owner: selectedItem?.checklistTemplate?.type === 'workplace' || selectedItem?.checklistTemplate?.type === 'wokrplace' ? 'Workplace' : 
               selectedItem?.checklistTemplate?.type === 'organization' ? 'Organization' : 'Unknown',
        frequency: selectedItem?.checklistTemplate?.frequency?.label || 'Weekly',
        date: selectedItem?.completedAt ? new Date(selectedItem.completedAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
    };

    // console.log(accessibility, "line 301");


    return (
        <Paper elevation={2} sx={{ p: 0, overflow: 'hidden' }}>

           
                <Box
                    sx={{
                        p: 2,
                        bgcolor: 'grey.100',
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'end',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    {/* <Typography variant="h5" component="h2">
                    {templateTitle}
                </Typography> */}



                    <Button
                        style={{
                            backgroundColor: items.length && (selectedItem?.checklistTemplate?.signoffRequired || false) && canApprove ? 'green' : 'gray',
                            color: 'white',
                            padding: '3px 15px',
                            textTransform: 'capitalize',
                        }}
                        disabled={items.length === 0 || !(selectedItem?.checklistTemplate?.signoffRequired || false) || !canApprove}
                        title={!canApprove ? 'You do not have permission to approve this checklist' : undefined}
                        onClick={async () => {
                            const { isValid, invalidRowIndices } = validateAllCells();
                            if (isValid) {
                                try {
                                    // Call the approve API with the dynamic ID from selectedItem
                                    if (selectedItem?.id) {
                                        await approveChecklistScheduleEvent(selectedItem.id);
                                        showToast('success', 'Checklist approved successfully');
                                        // Refresh categorized events after successful approval
                                        if (onApprovalSuccess) {
                                            onApprovalSuccess();
                                        }
                                        onBackClick && onBackClick();
                                    } else {
                                        showToast('error', 'Error: No checklist ID found');
                                    }
                                } catch (error: any) {
                                    console.error('Error approving checklist:', error);
                                    const errorMessage = error?.response?.data?.message || 
                                                       error?.message || 
                                                       'Failed to approve checklist. Please try again.';
                                    showToast('error', errorMessage);
                                }
                            } else {
                                const rowNumbers = invalidRowIndices.map((index) => index + 1).join(', ');
                                showToast('error', `Please fix validation errors in row${invalidRowIndices.length > 1 ? 's' : ''} ${rowNumbers} before approving`);
                            }
                        }}
                    >
                        Approve
                    </Button>

                </Box>

            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography variant="body1"><strong>Version:</strong> {headerData.version}</Typography>
                    <Typography variant="body1"><strong>Checklist Name:</strong> {headerData.checklistName}</Typography>
                    <Typography variant="body1"><strong>Category:</strong> {headerData.category}</Typography>
                    <Typography variant="body1"><strong>Owner:</strong> {headerData.owner}</Typography>
                    <Typography variant="body1"><strong>Frequency:</strong> {headerData.frequency}</Typography>
                    <Typography variant="body1"><strong>Date:</strong> {headerData.date}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                            checked={selectedItem?.checklistTemplate?.signoffRequired || false}
                            disabled
                            color="primary"
                            size="small"
                        />
                        <Typography variant="body1"><strong>Sign Off</strong></Typography>
                    </Box>
                </Box>
            </Box>

            <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    sx={{
                                        textAlign: column.id === 'section' ? 'left' : 'center',
                                        paddingLeft: column.id === 'section' ? '8px' : '1px',
                                    }}
                                >
                                    {column.header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item, index) => (
                            <React.Fragment key={item.id}>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={`${item.id}-${column.id}`}
                                            sx={{
                                                p: 1,
                                                textAlign: column.id === 'section' ? 'left' : 'center',
                                                paddingLeft: column.id === 'section' ? '8px' : '1px',
                                            }}
                                        >
                                            {column.id === 'correctAnswer' ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    {(item.isCorrect !== undefined ? item.isCorrect : item.submittedAnswer === item.correctAnswer) ? (
                                                        <CheckCircleIcon color="success" fontSize="small" />
                                                    ) : (
                                                        <CancelIcon color="error" fontSize="small" />
                                                    )}
                                                </Box>
                                            ) : column.id === 'picLink' ? (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={!item.picLink}
                                                    onClick={() => item.picLink && window.open(item.picLink, '_blank')}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    View Picture
                                                </Button>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {item[column.id as keyof ChecklistItem] as string}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={columns.length} sx={{ p: 1, bgcolor: 'grey.50' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: '100px' }}>
                                                Comments:
                                            </Typography>
                                            {item.isEditing ? (
                                                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        variant="outlined"
                                                        value={item.comment}
                                                        onChange={(e) => updateField(item.id, 'comment', e.target.value)}
                                                        error={!item.isValid.comment}
                                                        helperText={item.errors.comment}
                                                        inputRef={(el) => {
                                                            cellRefs.current[`${index}-comment`] = el;
                                                        }}
                                                        onFocus={() => handleCellFocus(index, 'comment')}
                                                        onKeyDown={(e: any) => handleKeyDown(e, index)}
                                                        sx={{
                                                            '& .MuiInputBase-root': {
                                                                height: '32px',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                padding: '6px 12px',
                                                                boxSizing: 'border-box',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: !item.isValid.comment ? 'error.main' : 'inherit',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        flexGrow: 1,
                                                    }}
                                                >
                                                    {item.comment || 'No comments'}
                                                </Typography>
                                            )}
                                            {!item.isValid.comment && (
                                                <Tooltip title={item.errors.comment || 'Invalid value'}>
                                                    <ErrorIcon color="error" fontSize="small" />
                                                </Tooltip>
                                            )}
                                            <Tooltip title={item.isEditing ? 'Cancel' : 'Edit Comment'}>
                                                <IconButton
                                                    size="small"
                                                    color={item.isEditing ? 'default' : 'primary'}
                                                    onClick={() => toggleEditMode(item.id)}
                                                >
                                                    {/* {item.isEditing ? (
                                                        <CancelIcon fontSize="small" style={{ color: 'red' }} />
                                                    ) : (
                                                        <Typography variant="body2" sx={{ color: 'primary.main' }}>
                                                            Edit
                                                        </Typography>
                                                    )} */}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {items.length === 0 && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        No items in this checklist.
                    </Typography>
                </Box>
            )}

            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {/* <Button
                        style={{
                            backgroundColor: items.length ? 'rgb(68, 99, 136)' : 'gray',
                            color: 'white',
                            padding: '5px 15px',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                        startIcon={<DownloadIcon />}
                        onClick={downloadJson}
                        disabled={items.length === 0}
                    >
                        Download JSON
                    </Button> */}
                </Box>
            </Box>

        </Paper>
    );
};

export default ApprovalTemplatePage;